import { Box, Button, Drawer } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import styled from "styled-components";
import enData from "../../translations/en/global.json";
import nlData from "../../translations/nl/global.json";
import { isMobile } from "react-device-detect";
import NavDrawerContent from "./NavDrawerContent";
import MenuIcon from "@mui/icons-material/Menu";
import HeaderWrapper from "./HeaderWrapper";
import NavLinks from "./NavLinks";
import NAV from "../../utils/nav.const";

interface NavProps {
  mobile: boolean;
  children: React.ReactNode;
}

const Nav = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const handleLanguageSwitch = (ln: string) => {
    i18n.changeLanguage(ln);

    localStorage.setItem("language", ln); // Save language to localStorage
  };

  return (
    <>
      {/* Top spacer */}
      <Box height={isMobile ? "5rem" : "10rem"}></Box>
      <PositionContainer mobile={isMobile}>
        <HeaderWrapper>
          <Navbar mobile={isMobile}>
            <Header mobile={isMobile}>
              <LogoSection>
                <MenuSection mobile={isMobile}>
                  <Button onClick={toggleDrawer}>
                    <MenuIcon fontSize="large" />
                  </Button>
                </MenuSection>
                <StyledDrawer open={open} onClose={toggleDrawer} anchor="top">
                  <NavDrawerContent
                    toggleDrawer={toggleDrawer}
                    languageSwitch={handleLanguageSwitch}
                  />
                </StyledDrawer>
                <NavigationLink
                  as="a"
                  href={process.env.REACT_APP_WORDPRESS_URL}
                >
                  <NavbarLogo
                    src={require("../../assets/images/logo/logo.png")}
                    alt="Rebidd Logo"
                    width=""
                    height={isMobile ? "18" : "27"}
                  />
                </NavigationLink>
              </LogoSection>
              <HeaderSection mobile={isMobile}>
                <NavLinks />
              </HeaderSection>

              <ProfileSection mobile={isMobile}>
                <LanguageButtons mobile={isMobile}>
                  <Button
                    onClick={() => handleLanguageSwitch("nl")}
                    sx={{ padding: 1, minWidth: 0 }}
                  >
                    <img
                      src={require("../../assets/images/nl.png")}
                      height="24"
                      width="24"
                      alt="Dutch"
                    />
                  </Button>
                  <Button
                    onClick={() => handleLanguageSwitch("en")}
                    sx={{ padding: 1, minWidth: 0 }}
                  >
                    <img
                      src={require("../../assets/images/en.png")}
                      height="24"
                      width="24"
                      alt="English"
                    />
                  </Button>
                </LanguageButtons>

                <NavLink to={`/${NAV.PROFILE}/${NAV.PROFILE_OVERVIEW}`}>
                  <svg viewBox="0 0 20 20" width={30}>
                    <path
                      fill="#5b41ff"
                      d="M10,10.9c2.373,0,4.303-1.932,4.303-4.306c0-2.372-1.93-4.302-4.303-4.302S5.696,4.223,5.696,6.594C5.696,8.969,7.627,10.9,10,10.9z M10,3.331c1.801,0,3.266,1.463,3.266,3.263c0,1.802-1.465,3.267-3.266,3.267c-1.8,0-3.265-1.465-3.265-3.267C6.735,4.794,8.2,3.331,10,3.331z"
                    ></path>
                    <path
                      fill="#5b41ff"
                      d="M10,12.503c-4.418,0-7.878,2.058-7.878,4.685c0,0.288,0.231,0.52,0.52,0.52c0.287,0,0.519-0.231,0.519-0.52c0-1.976,3.132-3.646,6.84-3.646c3.707,0,6.838,1.671,6.838,3.646c0,0.288,0.234,0.52,0.521,0.52s0.52-0.231,0.52-0.52C17.879,14.561,14.418,12.503,10,12.503z"
                    ></path>
                  </svg>
                </NavLink>
              </ProfileSection>
            </Header>
          </Navbar>
        </HeaderWrapper>
      </PositionContainer>
    </>
  );
};

export default Nav;

const LanguageButtons = styled.div<NavProps>`
  display: ${(props) => (props.mobile ? "none" : "flex")};
  margin-right: 2rem;
`;

const PositionContainer = styled.div<NavProps>`
  position: fixed;
  top: ${(props) => (props.mobile ? "0px" : "10px")};
  z-index: 1300;
  width: 100%;
`;

const StyledDrawer = styled(Drawer)`
  .css-4t3x6l-MuiPaper-root-MuiDrawer-paper {
    overflow: visible;
  }

  .MuiDrawer-paperAnchorTop {
    top: 30px;
  }
`;

const MenuSection = styled.div<NavProps>`
  display: ${(props) => (props.mobile ? "flex" : "none")};
`;

const Navbar = styled.div<NavProps>`
  background-color: #fff;
  margin: 0;
  top: 0;
  right: 0;
  margin-left: ${(props) => (props.mobile ? "0rem" : "-5rem")};
  margin-right: ${(props) => (props.mobile ? "0rem" : "-5rem")};
  border-radius: ${(props) => (props.mobile ? "0px" : "40px")};
  z-index: 900;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.2) !important;
`;

const Header = styled.div<NavProps>`
  height: ${(props) => (props.mobile ? "60px" : "5rem")};
  display: flex;
  justify-content: space-between;
  margin: ${(props) => (props.mobile ? "0px" : "0 2rem")};
`;

const HeaderSection = styled.div<NavProps>`
  display: ${(props) => (props.mobile ? "none" : "flex")};
  align-items: center;
  justify-content: center;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProfileSection = styled.div<NavProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: ${(props) => (props.mobile ? "24px" : "0px")};
`;

export const NavigationLink = styled(NavLink)`
  display: inline-block;
  color: #000;
  font-size: 15px;
  text-decoration: none;

  &:hover {
    color: #5b41ff;
  }
`;

const NavbarLogo = styled.img<{
  src: string;
  alt: string;
  width: string;
  height: string;
}>``;
